import firebase from "../../firebase/firebase";
import { DB_COLLECTIONS, DB_DEFAULTS } from "../../firebase/collections";
import { mapAppointment, mapUserToVisitorSnippet } from "../../utils/mapping";
import { Appointment, Stand, User, UserSnippet } from "../../data/interfaces";
import _environment from "../../_environment/_environment";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../utils/analytics";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------

export type AppointmentStandMember = Omit<UserSnippet, "id" | "image_url" | "description">;

/**
 * Fetches appointments list by user_id.
 * @function
 * @async
 *
 * @param uid
 * @param limit
 * @param last
 */
export const getUserAppointments = async (uid: string, limit?: number, last?: any) => {
  try {
    if (!uid) {
      return [];
    }

    // Load stands from firestore
    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.APPOINTMENTS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("visitor.uid", "==", uid)
        .orderBy("start_datetime", "desc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("visitor.uid", "==", uid)
        .orderBy("start_datetime", "desc")
        .limit(limit)
        .startAfter(last.start_datetime)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push(mapAppointment(doc.id, doc.data()));
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Creates a new appointment doc in firestore
 * @function
 * @async
 *
 * @param user
 * @param members
 * @param stand
 * @param start
 * @param end
 * @param notes
 */
export const createNewAppointment = async (
  user: User,
  members: AppointmentStandMember[],
  stand: Stand,
  start: string,
  end: string,
  notes: string
): Promise<void> => {
  try {
    // Compose visitor from user obj
    const visitor = mapUserToVisitorSnippet(user);

    // Compose stand snippet
    const standSnippet = {
      id: stand.id,
      name: stand.name,
      logo_image_url: stand.logo_image_url,
    };

    const appointment: Appointment = {
      visitor: visitor,
      stand: standSnippet,
      members: members,
      start_datetime: start,
      end_datetime: end,
      notes: notes,
      created_by: visitor.uid,
    };

    await firebase.firestore().collection(DB_COLLECTIONS.APPOINTMENTS).add(appointment);

    // Track event
    trackAnalyticsEvent(ANALYTICS_EVENTS.REQUEST_APPOINTMENT);
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Handles appointment response
 * @function
 * @async
 *
 * @param appointment
 * @param accepted
 */
export const sendAppointmentResponse = async (
  appointment: Appointment,
  accepted: boolean
): Promise<void> => {
  try {
    // TODO: GENERARE ANCHE L'URL MATTERMOST

    const body: any = {
      accepted,
    };
    if (accepted) {
      body.jitsi_url = `${_environment.jitsiURL}/${appointment.id}`;
    }

    // Update record on firestore
    await firebase
      .firestore()
      .collection(DB_COLLECTIONS.APPOINTMENTS)
      .doc(appointment.id)
      .update(body);

    // Track event
    let event = !!accepted
      ? ANALYTICS_EVENTS.RESPONSE_APPOINTMENT_OK
      : ANALYTICS_EVENTS.RESPONSE_APPOINTMENT_KO;

    trackAnalyticsEvent(event);
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};
